import type { StoryblokStory } from 'storyblok-generate-ts';

export type GetTranslatedStoryReturn = {
  default: {
    locale: string;
    url: string;
    title: string | null;
  };
  alternates: {
    locale: string;
    url: string;
    title: string | null;
  }[];
};

function splitSlug(slug: string | null): string {
  if (!slug) return '';
  const splittedSlug = slug.includes('/page/') ? slug.split('/page/')[1] : slug.split('page/')[1];
  return splittedSlug ?? '';
}

export function getTranslatedStory(
  originalStory: StoryblokStory<any>,
  locale: string,
): GetTranslatedStoryReturn {
  // Duplicated / demo spaces in Storyblok are missing the Translatable slug App
  if (!originalStory?.default_full_slug && !originalStory?.full_slug)
    return {
      default: {
        locale: 'nl',
        url: originalStory?.full_slug.split('/page/')?.[1] || '',
        title: originalStory?.name,
      },
      alternates: [],
    };

  const slug = originalStory?.default_full_slug || originalStory?.full_slug;

  const defaultUrl = {
    locale: 'nl',
    url: splitSlug(slug),
    title: originalStory?.name,
  };

  const translatedSlugs =
    originalStory?.translated_slugs?.filter((slug) => (slug as any).published) ?? [];

  const translatedUrls =
    translatedSlugs?.map((translatedSlug) => {
      const { lang, path, name } = translatedSlug;
      const urlRaw = path ? path : name;
      const url = splitSlug(urlRaw);

      return {
        locale: lang,
        url,
        title: name,
      };
    }) ?? [];

  const defaultObject = [defaultUrl, ...translatedUrls].find((obj) => obj.locale === locale) || {
    locale: locale,
    url: splitSlug(slug),
    title: '',
  };

  const alternateObjects =
    [defaultUrl, ...translatedUrls].filter((obj) => obj.locale !== locale) || [];

  const urls = {
    default: defaultObject,
    alternates: alternateObjects,
  };

  return urls;
}

export const getLocaleStoryUrl = (
  originalStory: StoryblokStory<any>,
  locale: string,
  localePrefix: string,
) => {
  const translatedStory = getTranslatedStory(originalStory, locale);

  return `${localePrefix}/${translatedStory.default.url}`;
};

export const getLocaleUrlFromFullSlug = (slug: string, localePrefix: string) => {
  const url = slug.includes('page/') ? slug.split('page/')[1] : slug;
  return `${localePrefix}/${url}`;
};
